import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReportsTable from "./ReportsTable";
import { removeDeductFromName } from "../../utils/helperFuctions";

const SalesReportTable = ({ reportData, onBack, reportType, dateRange, branch }) => {
  const navigate = useNavigate();
  const [showGroupedModal, setShowGroupedModal] = useState(false);
  const [selectedGroupedEvents, setSelectedGroupedEvents] = useState([]);
  const [selectedGroupQty, setSelectedGroupQty] = useState(null);

  // Derive the original/base rental days before extensions/reductions.
  // totalQty is the net days shown in the main table (base + extensions - reductions).
  const computeBaseRentalDays = (events, totalQty) => {
    if (totalQty == null) return null;
    const total = Number.parseFloat(totalQty);
    if (!Number.isFinite(total)) return null;

    if (!Array.isArray(events) || events.length === 0) {
      return total;
    }

    let extensionDays = 0;
    let reductionDays = 0;

    for (const ev of events) {
      if (!ev || !ev.type) continue;
      const isExtension = ev.type === "Extension";
      const isReduction = ev.type === "Reduction";
      if (!isExtension && !isReduction) continue;

      const rawDays = ev?.time_adjustment?.days;
      const daysNum = Number.parseFloat(rawDays);
      if (!Number.isFinite(daysNum)) continue;

      const absDays = Math.abs(daysNum);
      if (isExtension) {
        extensionDays += absDays;
      } else if (isReduction) {
        reductionDays += absDays;
      }
    }

    const base = total - extensionDays + reductionDays;
    return Number.isFinite(base) ? base : null;
  };

  const baseRentalDays = computeBaseRentalDays(selectedGroupedEvents, selectedGroupQty);

  const normalizeItem = (name) => {
    const cleaned = removeDeductFromName(name || "");
    return cleaned.trim().toUpperCase() === "ADDITIONAL DELIVERY" ? "Delivery Charges" : cleaned;
  };

  const columns = [
    { 
      header: "Reservation ID", 
      accessor: "reservationId",
      render: (row) => {
        if (!row.reservationId) return "";
        return (
          <button
            onClick={() => navigate(`/reservations/${row.reservationId}`)}
            className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors cursor-pointer"
          >
            {row.reservationId}
          </button>
        );
      }
    },
    { header: "Status", accessor: "status" },
    { header: "Monthly Billing", accessor: "monthlyBilling" },
    { header: "Post?", accessor: "post" },
    { header: "Entry Date", accessor: "entryDate" },
    { header: "Due Date", accessor: "entryDate" }, //can be changed to dueDate
    { header: "Invoice / Bill Number", accessor: "invoiceNumber" },
    { header: "Transaction Type", accessor: "transactionType" },
    { 
      header: "Customer", 
      accessor: "customer",
      render: (row) => {
        if (!row.customer) return "";
        // Use customerId if available, otherwise navigate to customers list
        const customerId = row.customerId;
        if (customerId) {
          return (
            <button
              onClick={() => navigate(`/customers/${customerId}`)}
              className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors cursor-pointer"
            >
              {row.customer}
            </button>
          );
        }
        // Fallback to customers list if no ID
        return (
          <button
            onClick={() => navigate(`/customers`)}
            className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors cursor-pointer"
          >
            {row.customer}
          </button>
        );
      }
    },
    { header: "Supplier", accessor: "supplier", empty: true },
    { header: "Tax Type", accessor: "taxType" },
    { header: "Currency Code", accessor: "currencyCode" },
    { header: "Item", accessor: "item", render: (row) => normalizeItem(row.item) },
    { 
      header: "Description", 
      accessor: "description",
      render: (row) => row.description || normalizeItem(row.item)
    },
    { 
      header: "Qty", 
      accessor: "qty",
      render: (row) => {
        const hasGrouped = row.groupedCount > 1;
        // Disable grouping UI for rental-days/time-adjustment rows; backend
        // now exposes a synthetic "Grouped Rental Days" line for display.
        const normalizedItem = normalizeItem(row.item);
        const isRentalRow = normalizedItem === "Rental Days";
        if (!hasGrouped || isRentalRow) return row.qty;
        
        return (
          <div className="flex items-center gap-2">
            <span>{row.qty}</span>
            <button
              onClick={() => {
                setSelectedGroupedEvents(row.groupedEvents || []);
                setSelectedGroupQty(row.qty ?? null);
                setShowGroupedModal(true);
              }}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
              title={`View ${row.groupedCount} grouped events`}
            >
              Grouped [{row.groupedCount}]
            </button>
          </div>
        );
      }
    },
    { header: "Discount %", accessor: "discount" },
    { header: "Unit Price", accessor: "unitPrice" },
    { header: "Account", accessor: "account", empty: true },
    { header: "Location", accessor: "location" },
    { 
      header: "Class", 
      accessor: "class",
      render: (row) => {
        if (!row.class) return "";
        // Use vehicleId if available, otherwise navigate to vehicles list
        const vehicleId = row.vehicleId;
        if (vehicleId) {
          return (
            <button
              onClick={() => navigate(`/vehicles/${vehicleId}`)}
              className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors cursor-pointer"
            >
              {row.class}
            </button>
          );
        }
        // Fallback to vehicles list if no ID
        return (
          <button
            onClick={() => navigate(`/vehicles`)}
            className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors cursor-pointer"
          >
            {row.class}
          </button>
        );
      }
    },
    { header: "Tax Rate", accessor: "taxRate"},
    { header: "Payment Reference", accessor: "paymentReference", empty: true },
    { header: "Payment Date", accessor: "paymentDate", empty: true },
    { header: "Payment From Account", accessor: "paymentFromAccount", empty: true },
    { header: "Payment Amount", accessor: "paymentAmount", empty: true },
    { header: "Salesman", accessor: "salesman" },
    { header: "Captain", accessor: "captain" },
  ];

  // Custom row styling function to handle deleted billing events
  const getRowClassName = (row, defaultClass) => {
    // Apply light red background for deleted billing events
    if (row.billingEventStatus === 'deleted') {
      return 'bg-red-50 hover:bg-red-100';
    }
    // Use default grouping background for other rows
    return `${defaultClass} hover:bg-gray-100`;
  };

  return (
    <>
      <ReportsTable
        title={reportType === "sales" ? "Sales Report" : "Transactions Report"}
        branch={branch}
        dateRange={dateRange}
        reportData={reportData}
        columns={columns}
        exportEndpoint="/api/reports/sales"
        onBack={onBack}
        emptyStateSubtitle="No records found for the selected criteria."
        groupBy={(row) => row.reservationId}
        getRowClassName={getRowClassName}
      />
      
      {/* Grouped Events Modal */}
      {showGroupedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Grouped Billing Events ({selectedGroupedEvents.length} items)
              </h3>
              <button
                onClick={() => {
                  setShowGroupedModal(false);
                  setSelectedGroupedEvents([]);
                  setSelectedGroupQty(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sequence
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Business Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Credit Note
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedGroupedEvents.map((event, index) => (
                      <tr key={event.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {event.id}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {event.type}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {parseFloat(event.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {(() => {
                            // For time adjustments, show the days
                            if (event.type === 'Extension' || event.type === 'Reduction') {
                              const days = event?.time_adjustment?.days;
                              if (days == null) return '';
                              const numDays = Number.parseFloat(days);
                              return Number.isFinite(numDays) ? Math.abs(numDays) : days;
                            }
                            // For grouped rental days, show the original/base rental days
                            if (event.type === 'Rental Days') {
                              if (baseRentalDays != null) {
                                return baseRentalDays;
                              }
                              if (selectedGroupQty != null) {
                                return selectedGroupQty;
                              }
                              return '';
                            }
                            return '';
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {event.sequence}{event.sequence_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {event.business_date ? new Date(event.business_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            event.status === 'locked' ? 'bg-green-100 text-green-800' :
                            event.status === 'deleted' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {event.is_credit_note ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowGroupedModal(false);
                  setSelectedGroupedEvents([]);
                  setSelectedGroupQty(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesReportTable;